import type {
  FormValues,
  MutationError,
  MutationPayload,
  QueryContextKey,
  QueryData,
  VendorRow,
  VendorsResponse
} from './types'
import type { ListState, ListQuerySortField, ListQuerySortOrder, Location } from '../premium/types'
import type { TableColumnsType } from 'antd'

import {
  CaretDownOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteFilled,
  SearchOutlined,
  SyncOutlined
} from '@ant-design/icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, Dropdown, Form, Input, Space, Table, Tooltip } from 'antd'
import { isAxiosError } from 'axios'
import cx from 'classnames'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDebounce, useInterval } from 'react-use'

import $api, { API_URL } from '../../API'
import { PushType, mapper as pushTypeNameMapper } from '../../domain/push-type.enum'
import './index.css'
import { CellTitle } from '../premium/components/cell-title'
import { EmptyCellValue } from '../premium/components/empty-cell-value'
import { ExpandIcon } from '../premium/components/expand-icon'
import { currency, duration } from '../premium/formatters'

import { BooleanCell } from './components/boolean-cell'
import { ExpandedRow } from './components/expanded-row'
import { baseCellClassName, baseHeaderCellClassName, flags } from './constants'
import { ValidationError } from './errors/validation-error'
import { useStore } from './store'

const initialValues: FormValues = {
  name: '',
  id: '',
  is_active: false,
  current_ar: 0,
  open_ar: 0,
  location: {
    short_name: '',
    id: ''
  }
}

export function VendorsPage(): JSX.Element {
  const [form] = Form.useForm<FormValues>()
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])

  useEffect(() => {
    form.setFieldsValue(initialValues)
  }, [form])

  const [state, dispatch] = useStore()

  const [debouncedQuery, setDebouncedQuery] = useState(state.searchParams.query)

  useDebounce(
    () => {
      const query = (state.searchParams.query ?? '').trim().toLowerCase()

      setDebouncedQuery(query)
      dispatch({ type: 'set-pagination', payload: { ...state.pagination, currentPage: 0 } })
    },
    500,
    [state.searchParams.query]
  )

  const query = useQuery<unknown, unknown, QueryData, QueryContextKey>({
    queryKey: [
      'vendors',
      state.sort,
      state.pagination,
      { ...state.searchParams, query: debouncedQuery }
    ],

    refetchOnMount: true,

    async queryFn(context) {
      const [, sort, pagination, searchParams] = context.queryKey
      const { pageSize, currentPage } = pagination
      const {
        query = '',
        locationIds,
        fundingTypes,
        currentAr,
        openAr,
        schedule,
        isActive,
        flags,
        nextPullDate
      } = searchParams

      const searchFields = ['vendor_name', 'vendor_id']

      const sp = new URLSearchParams([
        ...(currentPage > 0 ? [['page', currentPage.toString(10)]] : []),
        ['page-size', pageSize.toString(10)],
        ...(typeof sort.field === 'string' ? [['order-by', sort.field]] : []),
        ...(typeof sort.order === 'string' ? [['direction', sort.order]] : []),
        ...(locationIds.length > 0 ? locationIds.map(id => ['location-id', id]) : []),
        ...(fundingTypes.length > 0 ? fundingTypes.map(ft => ['funding-type', ft]) : []),
        ...(currentAr.length > 0 ? currentAr.map(ar => ['current-ar', ar.toString(10)]) : []),
        ...(openAr.length > 0 ? openAr.map(ar => ['open-ar', ar.toString(10)]) : []),
        ...(schedule.length > 0 ? schedule.map(day => ['schedule-day', day.toString(10)]) : []),
        ...(isActive.length > 0 ? isActive.map(active => ['is-active', active.toString()]) : []),
        ...(query.length > 0
          ? [['query', query], ...searchFields.map(name => ['search-fields', name])]
          : []),
        ...flags.map(flag => ['flag', flag]),
        ...nextPullDate.map(date => ['next-pull-date', date])
      ])

      const { data } = await $api.get<VendorsResponse>(`${API_URL}/vendors`, {
        params: sp
      })

      return {
        rows: data.rows,
        totalFiltered: data.total_filtered,
        totalUnfiltered: data.total_unfiltered
      }
    },

    onSuccess() {
      dispatch({ type: 'clear-expanded-row-id' })
    },

    keepPreviousData: true
  })

  const mutation = useMutation<unknown, MutationError, MutationPayload>({
    async mutationFn({ id, ...data }: MutationPayload) {
      const isNew = id === 'new'

      const url = isNew ? '/vendors' : `/vendors/${id}`

      try {
        await $api.request({
          url,
          method: isNew ? 'POST' : 'PATCH',
          data
        })
        dispatch({ type: 'clear-expanded-row-id' })
        void query.refetch()
      } catch (e) {
        if (isAxiosError(e)) {
          if (e.response?.status === 422) {
            const data = e.response.data

            throw new ValidationError(data.issues)
          }

          throw new Error(e.message)
        }
      }
    }
  })

  const locationsQuery = useQuery<unknown, unknown, Location[]>({
    queryKey: ['locations'],

    refetchOnMount: true,

    async queryFn() {
      try {
        const response = await $api.get('/locations')

        const data = await response.data
        return data
      } catch (e) {
        if (isAxiosError(e)) {
          throw new Error(`Request failed with status ${e.status} (${e.message})`)
        }
      }
    }
  })

  const columns = useMemo<TableColumnsType<VendorRow>>(
    () => [
      {
        title({ sortColumns }) {
          return (
            <CellTitle id='vendor_id' sortColumns={sortColumns}>
              VENDOR ID
            </CellTitle>
          )
        },
        key: 'vendor_id',
        dataIndex: 'vendor_id',
        sorter: true,
        showSorterTooltip: false,
        width: '12%',
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title({ sortColumns }) {
          return (
            <CellTitle id='name' sortColumns={sortColumns}>
              Name
            </CellTitle>
          )
        },
        key: 'name',
        dataIndex: 'name',
        sorter: true,
        showSorterTooltip: false,
        width: '15%',
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title: 'Location',
        key: 'location',
        dataIndex: ['location', 'short_name'],
        width: '7%',
        align: 'center',

        filters: locationsQuery.data
          ?.map(location => {
            return {
              value: location.id,
              name: location.name,
              short_name: location.short_name
            }
          })
          .map(filter => ({
            text: (
              <div
                className={cx(
                  'flex justify-between items-center gap-6',
                  filter.value === null && 'text-gray-400'
                )}
              >
                <span>
                  {filter.name} <span className='text-gray-500'>({filter.short_name})</span>
                </span>
              </div>
            ),
            value: filter.value
          })),

        filterMultiple: true,
        filteredValue: state.searchParams.locationIds,

        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title: 'Active',
        key: 'is_active',
        dataIndex: 'is_active',
        render: BooleanCell<VendorRow>,
        width: '7%',
        align: 'center',
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title({ sortColumns }) {
          return (
            <CellTitle id='current_ar' sortColumns={sortColumns}>
              Current A/P
            </CellTitle>
          )
        },
        key: 'current_ar',
        dataIndex: 'current_ar',
        align: 'right',
        sorter: true,
        showSorterTooltip: false,
        render(value: number | null | undefined) {
          if (typeof value !== 'number') {
            return <EmptyCellValue />
          }

          return <span>{currency(value ?? 0)}</span>
        },
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title({ sortColumns }) {
          return (
            <CellTitle id='open_ar' sortColumns={sortColumns}>
              Open A/P
            </CellTitle>
          )
        },
        key: 'open_ar',
        dataIndex: 'open_ar',
        align: 'right',
        sorter: true,
        showSorterTooltip: false,
        render(value: number | null | undefined) {
          if (typeof value !== 'number') {
            return <EmptyCellValue />
          }

          return <span>{currency(value ?? 0)}</span>
        },
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title: 'Funding Type',
        key: 'funding_type',
        dataIndex: 'funding_type',
        align: 'center',
        render(value) {
          return pushTypeNameMapper[value as PushType] ?? value ?? <EmptyCellValue />
        },
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title: 'Flags',
        key: 'flags',
        dataIndex: 'flags',
        align: 'center',
        render(value: string[]) {
          if (!value?.length) {
            return <EmptyCellValue />
          }

          return (
            <Space>
              {value.map(v => {
                const flag = flags.find(flag => flag.value === v)

                if (!flag) {
                  return null
                }

                return (
                  <Tooltip title={flag.text} key={flag.value}>
                    <flag.icon className={flag.className} />
                  </Tooltip>
                )
              })}
            </Space>
          )
        },
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      }
    ],
    [locationsQuery.data, state.searchParams]
  )

  const handleSave = useCallback(() => {
    const { id } = state.expandedRow
    const values = form.getFieldsValue()

    if (id === undefined) {
      return
    }

    mutation.mutate({
      id,
      funding_type: values.funding_type ?? null,
      account_number: values.account_number ?? '',
      routing_number: values.routing_number ?? ''
    })
  }, [form, mutation, state.expandedRow])

  const handleCancel = useCallback(() => {
    form.setFieldsValue(initialValues)
    dispatch({ type: 'clear-expanded-row-id' })
  }, [form, dispatch])

  function handleRowExpand(expanded: boolean, record: VendorRow): void {
    if (!expanded) {
      form.setFieldsValue(initialValues)
      dispatch({ type: 'clear-expanded-row-id' })
      return
    }

    form.setFieldsValue({
      name: record.name,
      id: record.id,
      location: record.location,
      funding_type: record.funding_type ?? undefined,
      schedule_day: record.schedule_day ?? undefined,
      is_active: record.is_active ?? false,

      current_ar: record.current_ar ?? 0,
      open_ar: record.open_ar ?? 0,
      routing_number: record.routing_number,
      account_number: record.account_number
    })

    dispatch({ type: 'set-expanded-row-id', payload: record.id })
  }

  const isDefaultFilters = useMemo(() => {
    return !Object.values(state.searchParams).some(value => value.length > 0)
  }, [state.searchParams])

  useInterval(() => {
    void query.refetch()
  }, state.refreshInterval ?? null)

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys)
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange
  }

  return (
    <>
      <div className='flex flex-col gap-4 p-6 font-normal rounded-lg font-lato bg-lucent-background text-lucent-text'>
        <div className='flex justify-between items-start'>
          <div className='flex gap-6 items-center'>
            <h1 className='text-2xl'>Premium Payments Vendor Management</h1>
          </div>
        </div>

        <div className='flex flex-col rounded-lg border'>
          <div className='flex flex-col'>
            <div className='flex flex-row gap-2 items-center p-4'>
              <div className='flex-grow'>
                <Input
                  placeholder='Search groups'
                  className='font-lato text-sm py-1 bg-gray-50 [&_input]:bg-transparent rounded [&_input]:placeholder:text-gray-400'
                  prefix={<SearchOutlined className='text-gray-500' />}
                  suffix={
                    (state.searchParams.query.length ?? 0) > 0 ? (
                      <button
                        type='button'
                        className='flex justify-center items-center p-2 -m-2 border'
                        onClick={() => {
                          dispatch({
                            type: 'set-query',
                            payload: ''
                          })
                        }}
                      >
                        <CloseOutlined size={16} className='text-gray-500' />
                      </button>
                    ) : undefined
                  }
                  value={state.searchParams.query ?? ''}
                  onChange={event => {
                    dispatch({
                      type: 'set-query',
                      payload: event.target.value
                    })
                  }}
                />
              </div>

              <Space>
                {!isDefaultFilters && (
                  <Button
                    type='default'
                    onClick={() => {
                      dispatch({
                        type: 'clear-filters'
                      })
                    }}
                  >
                    <span className='mr-2 -translate-y-[4px]'>
                      <DeleteFilled className='text-lucent-primary' />
                    </span>
                    <span className='font-lato font-bold text-lucent-primary text-[10px] uppercase -translate-y-[1px]'>
                      Clear Filters
                    </span>
                  </Button>
                )}

                <Dropdown.Button
                  type='default'
                  disabled={query.isFetching}
                  onClick={() => {
                    void query.refetch()
                  }}
                  icon={
                    <CaretDownOutlined
                      className={cx(
                        'w-[12px] h-[12px] !translate-y-[2px]',
                        typeof state.refreshInterval === 'number' && 'text-lucent-primary',
                        typeof state.refreshInterval !== 'number' && 'text-lucent-gray'
                      )}
                    />
                  }
                  menu={{
                    items: [30, 60, 60 * 5, 60 * 30].map(seconds => ({
                      key: `refresh-${seconds}`,
                      itemIcon() {
                        if (state.refreshInterval === seconds * 1000) {
                          return <CheckOutlined className='ml-2 text-lucent-primary' />
                        }

                        return null
                      },
                      onClick() {
                        if (state.refreshInterval === seconds * 1000) {
                          dispatch({
                            type: 'clear-refresh-interval'
                          })

                          return
                        }

                        dispatch({
                          type: 'set-refresh-interval',
                          payload: seconds * 1000
                        })
                      },
                      label: duration(seconds * 1000)
                    }))
                  }}
                >
                  <div className='flex items-center space-x-1.5'>
                    <SyncOutlined
                      className={cx(
                        query.isFetching && 'animate-custom-spin text-lucent-gray',
                        !query.isFetching && 'text-lucent-primary'
                      )}
                    />

                    <span
                      className={cx(
                        'font-lato font-bold text-[10px] uppercase',
                        query.isFetching && 'text-lucent-gray',
                        !query.isFetching && 'text-lucent-primary'
                      )}
                    >
                      <span>Refresh</span>

                      {typeof state.refreshInterval === 'number' && (
                        <span className=''>
                          <span className='mx-1.5'>|</span>
                          Auto {duration(state.refreshInterval, { short: true })}
                        </span>
                      )}
                    </span>
                  </div>
                </Dropdown.Button>
              </Space>
            </div>
          </div>

          <Table
            size='small'
            columns={columns}
            rowKey='id'
            dataSource={query.data?.rows ?? []}
            loading={query.isFetching}
            className='rounded-none border-t payment-files-table'
            rowSelection={rowSelection}
            expandable={{
              expandedRowKeys:
                typeof state.expandedRow.id === 'string' ? [state.expandedRow.id] : [],
              expandedRowRender(row) {
                return (
                  <ExpandedRow
                    row={row}
                    form={form}
                    isSubmitting={mutation.isLoading}
                    availableLocations={locationsQuery.data ?? []}
                    error={mutation.error ?? undefined}
                    onSave={handleSave}
                    onCancel={handleCancel}
                  />
                )
              },
              expandIcon: ExpandIcon,
              onExpand: handleRowExpand
            }}
            onRow={record => {
              return {
                className: cx({
                  'bg-purple-50 !hover:bg-purple-50': record.open_ar !== record.current_ar
                })
              }
            }}
            pagination={{
              current: state.pagination.currentPage + 1,
              pageSize: state.pagination.pageSize,
              pageSizeOptions: ['10', '25', '50', '100'],
              showSizeChanger: true,
              total: query.data?.totalFiltered ?? 0,
              showTotal(total, [from, to]) {
                const parts = [
                  `Showing ${from}–${to} of ${total} ${total === 1 ? 'item' : 'items'}`
                ]

                if (
                  typeof query.data?.totalUnfiltered === 'number' &&
                  query.data.totalUnfiltered !== total
                ) {
                  parts.push(
                    `Total ${query.data.totalUnfiltered} ${query.data.totalUnfiltered === 1 ? 'item' : 'items'}`
                  )
                }

                return <span className='text-sm text-gray-500 font-lato'>{parts.join(' • ')}</span>
              },
              hideOnSinglePage: false
            }}
            onChange={(pagination, filters, sorter, extra) => {
              if (extra.action === 'paginate') {
                dispatch({
                  type: 'set-pagination',
                  payload: {
                    pageSize: pagination.pageSize ?? 10,
                    currentPage: pagination.current ? pagination.current - 1 : 0
                  }
                })
              }

              if (extra.action === 'sort') {
                const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter

                dispatch({
                  type: 'set-sort',
                  payload: {
                    field: singleSorter?.column?.key as ListQuerySortField | undefined,
                    order:
                      typeof singleSorter?.order === 'string'
                        ? ({ ascend: 'asc', descend: 'desc' }[
                            singleSorter.order
                          ] as ListQuerySortOrder)
                        : undefined
                  }
                })
              }

              if (extra.action === 'filter') {
                dispatch({
                  type: 'set-filters',
                  payload: {
                    locationIds: (filters.location ??
                      []) as ListState['searchParams']['locationIds'],
                    fundingTypes: (filters.funding_type ??
                      []) as ListState['searchParams']['fundingTypes'],
                    openAr: (filters.open_ar ?? []) as ListState['searchParams']['openAr'],
                    currentAr: (filters.current_ar ?? []) as ListState['searchParams']['currentAr'],
                    schedule: (filters.schedule_day ?? []) as ListState['searchParams']['schedule'],
                    isActive: (filters.is_active ?? []) as ListState['searchParams']['isActive'],
                    flags: (filters.flags ?? []) as ListState['searchParams']['flags'],
                    pushTypes: (filters.push_type ?? []) as ListState['searchParams']['pushTypes'],
                    networks: (filters.network ?? []) as ListState['searchParams']['networks'],
                    nextPullDate: (filters.next_pull_date ??
                      []) as ListState['searchParams']['nextPullDate']
                  }
                })
              }
            }}
          />
        </div>
      </div>
    </>
  )
}
